import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { CompanyWhitelist } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { DeactivateMobileUUIDDialogComponent } from '../deactivate-mobile-uuiddialog/deactivate-mobile-uuiddialog.component';
import { DomSanitizer } from '@angular/platform-browser';
import { WhitelistScreens } from '../user-main-tab/user-main-tab.component';
import { SelectedButton } from 'src/app/components/button-toggle/button-toggle.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import { ButtonToggleComponent } from '../../../components/button-toggle/button-toggle.component';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
	BillingStartDateFilterPipe,
	RequestTimeToLocal,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-user-whitelist',
	templateUrl: './user-whitelist.component.html',
	styleUrls: ['./user-whitelist.component.scss', '../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		ButtonToggleComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatChipSet,
		MatChip,
		NgClass,
		MatIcon,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		BillingStartDateFilterPipe,
		RequestTimeToLocal,
		DataSetLoadingRowComponent,
	],
})
export class UserWhitelistComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@Output() whitelistPageSelectionChanged =
		new EventEmitter<WhitelistScreens>();
	/** When set, scopes whitelist list/mutations to this company instead of active company. */
	@Input() companyId?: string;
	/**
	 * When false, hide Accept/Decline/Add Note/Deactivate (Support/Billing view-only).
	 * Default true preserves top-level /users CA behavior when input is omitted.
	 */
	@Input() allowMutate = true;

	displayedColumns: string[] = [
		'mobileUUID',
		'status',
		'utcRequestTime',
		'note',
		'options',
	];
	dataSource = new MatTableDataSource<CompanyWhitelist>();

	filterLabels = {
		defaultPlaceholder: $localize`:@@userWhitelist-defaultPlaceholder:Search UUID`,
		mobileUUID: $localize`:@@userWhitelist-filterLabelMobileID:Mobile ID`,
		status: $localize`:@@userWhitelist-filterLabelStatus:Status`,
		requestTime: $localize`:@@userWhitelist-filterLabelRequestTime:Request Time`,
		note: $localize`:@@userWhitelist-filterLabelNote:Note`,
	};

	statusFilterOptions: DropdownOption[] = [
		{ label: $localize`:@@userWhitelist-active:Active`, value: '1' },
		{ label: $localize`:@@userWhitelist-pending:Pending`, value: '2' },
	];

	private filterSubscription?: Subscription;
	activeCompanyId: string = '';
	currentFilters: QueryParam[] = [];
	userProfile?: UserProfile;

	getWhitelistUserListForCompanyLoadingKey =
		ApiLoadingKeys.getWhitelistUserListForCompany;
	finishedLoadingInitData: boolean = false;

	onAddNoteFocusRowId: string = '';

	selectedButton: SelectedButton = SelectedButton.left;

	constructor(
		private apiService: ApiService,
		private router: Router,
		private scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		private readonly dialog: MatDialog,
		private sanitizer: DomSanitizer
	) {}

	async ngAfterViewInit() {
		this.userProfile = await this.apiService.getUserProfile();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					this.activeCompanyId =
						this.userProfile?.activeRoleDetails?.company?.id!;
					const findTimeUTCIndex = filter.findIndex(
						(f) => f.param === 'utcRequestTime'
					);
					if (findTimeUTCIndex !== -1) {
						const utcRequestTime = DateTime.fromJSDate(
							filter[findTimeUTCIndex].value as unknown as Date
						)
							?.toUTC()
							.toString();
						filter[findTimeUTCIndex].value = utcRequestTime;
					}
					this.currentFilters = filter;
					const result = await this.apiService.getWhitelistUserListForCompany(
						filter,
						this.companyId
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((users) => {
				this.dataSource.data = users;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	capitalizeFirstLetter(status: string) {
		if (!status || typeof status !== 'string') {
			return '';
		}
		return status.charAt(0).toUpperCase() + status.slice(1);
	}

	deactivateWhitelist(row: CompanyWhitelist) {
		if (!this.allowMutate) {
			return;
		}
		const dialogRef: MatDialogRef<DeactivateMobileUUIDDialogComponent> =
			this.dialog.open(DeactivateMobileUUIDDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					user: row,
					companyId: this.companyId,
				},
			});
		this.dataSet.clearAllSelections();

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			}
		});
	}

	getChipStyling(status: string): string {
		switch (status) {
			case 'pending':
				return 'status-chip-pending';
			case 'inactive':
				return 'status-chip-inactive';
			case undefined:
				return 'status-chip-active';
		}
		return 'status-chip-active';
	}

	focusOnInput(row: CompanyWhitelist) {
		if (!this.allowMutate) {
			return;
		}
		this.onAddNoteFocusRowId = row.id;
		setTimeout(() => {
			document.getElementById(`note-input-${row.id}`)?.focus();
		}, 300);
	}

	async unfocusInput(event: any, row: CompanyWhitelist) {
		if (!this.allowMutate) {
			this.onAddNoteFocusRowId = '';
			return;
		}
		await this.updateNote(event, row);
		this.onAddNoteFocusRowId = '';
	}

	async checkInputOnKeyDown(event: KeyboardEvent, row: CompanyWhitelist) {
		if (event.key === 'Tab') {
			// If the tab key is pressed, always allow this and unfocus the input
			await this.updateNote(event, row);
			return;
		} else if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
			// Check if enter pressed (+ isn't pressed in conjunction with ctrl or shift), if so save the entry (and unfocus the input)
			event.preventDefault();
			this.blurAll();
			await this.updateNote(event, row);
			return;
		}

		// Also check if character requirement is satisfied
		const target = event.target as HTMLElement;
		const textContent = target.innerText || '';
		const textWithoutCarriageReturns = textContent.replace(/\n/g, '');
		if (
			textWithoutCarriageReturns.length >= 100 &&
			event.key.length === 1 // do not prevent keydowns like backspaces
		) {
			event.preventDefault();
		}
	}

	async updateNote(event: any, row: CompanyWhitelist) {
		if (!this.allowMutate) {
			return;
		}
		if (event.target.innerText.trim() !== '') {
			if (event.target.innerText !== row.note) {
				await this.apiService.updateOrAddNoteFromWhitelist(
					row.id,
					{
						note: event.target.innerText,
					},
					this.companyId
				);
				row.note = event.target.innerText;
			}
		} else {
			if (row.note) {
				await this.apiService.updateOrAddNoteFromWhitelist(
					row.id,
					{
						note: undefined,
					},
					this.companyId
				);
			}
			row.note = undefined;
		}
	}

	// Sanitize input before displaying in HTML to avoid security risks
	formatNoteNewLines(note: string) {
		const textWithBreaks = note.replace(/\n/g, '<br/>');
		return this.sanitizer.sanitize(1, textWithBreaks) || '';
	}

	async acceptWhitelist(row: CompanyWhitelist) {
		if (!this.allowMutate) {
			return;
		}
		const activeStatusId = '1';
		const activeStatusString = 'active';
		await this.apiService.updateWhitelistStatus(
			row.id,
			{
				statusId: activeStatusId,
			},
			this.companyId
		);
		// Keep record in the list and only update some of its attributes.
		row.status = activeStatusString;
		row.statusId = activeStatusId;
	}

	async declineWhitelist(row: CompanyWhitelist) {
		if (!this.allowMutate) {
			return;
		}
		const inactiveStatusId = '3';
		await this.apiService.updateWhitelistStatus(
			row.id,
			{
				statusId: inactiveStatusId,
			},
			this.companyId
		);
		// Remove record from the list
		this.dataSource.data = await this.apiService.getWhitelistUserListForCompany(
			this.currentFilters,
			this.companyId
		);
	}

	blurAll() {
		const tmp = document.createElement('input');
		document.body.appendChild(tmp);
		tmp.focus();
		document.body.removeChild(tmp);
	}

	// IMPORTANT: the below two functions are in mobile as well. If changing the
	// logic, then mobile will need changes as well.
	formatWhitelistIdForDisplay(input: string) {
		// Ensure the input string has the correct length
		if (input.length >= 8) {
			return input.substring(0, 4) + '...' + input.substring(input.length - 4);
		} else {
			return input;
		}
	}

	getSCService() {
		return this.scService;
	}

	getApiService() {
		return this.apiService;
	}

	getDialog() {
		return this.dialog;
	}

	whitelistPageChanged(selectedButton: SelectedButton) {
		if (selectedButton === SelectedButton.left) {
			this.whitelistPageSelectionChanged.emit(
				WhitelistScreens.whitelistDevices
			);
		} else {
			this.whitelistPageSelectionChanged.emit(WhitelistScreens.whitelistUsers);
		}
	}

	rowId(row: CompanyWhitelist): string {
		return row?.id || 'unknown-id';
	}
}
